'use client';

import React from 'react';
import { ChevronDown } from 'lucide-react';
import { CONTACT_FIELDS, ColumnMapping } from './importTypes';

interface ColumnMappingPanelProps {
  csvColumns: string[];
  mappings: ColumnMapping[];
  onMappingChange: (csvColumn: string, contactField: string) => void;
  sampleRows: Record<string, string>[];
}

export default function ColumnMappingPanel({
  csvColumns,
  mappings,
  onMappingChange,
  sampleRows,
}: ColumnMappingPanelProps) {
  const getMappingFor = (csvCol: string) =>
    mappings.find((m) => m.csvColumn === csvCol)?.contactField ?? '';

  const usedFields = mappings
    .filter((m) => m.contactField !== '')
    .map((m) => m.contactField);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Map CSV Columns to Contact Fields</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            We auto-detected mappings where possible. Adjust any that look incorrect.
          </p>
        </div>
        <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-md">
          {csvColumns.length} columns detected
        </span>
      </div>

      <div className="border border-border rounded-xl overflow-hidden">
        <div className="grid grid-cols-3 gap-0 bg-muted/60 px-4 py-2.5 border-b border-border">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">CSV Column</span>
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Maps To</span>
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Sample Value</span>
        </div>

        <div className="divide-y divide-border">
          {csvColumns.map((col) => {
            const currentMapping = getMappingFor(col);
            const sample = sampleRows[0]?.[col] ?? '';

            return (
              <div key={col} className="grid grid-cols-3 gap-4 items-center px-4 py-3 hover:bg-muted/20 transition-colors">
                {/* CSV column name */}
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-mono bg-muted px-2 py-0.5 rounded text-foreground truncate max-w-[140px]" title={col}>
                    {col}
                  </span>
                </div>

                {/* Field selector */}
                <div className="relative">
                  <select
                    value={currentMapping}
                    onChange={(e) => onMappingChange(col, e.target.value)}
                    className={`w-full text-xs rounded-lg border px-2.5 py-1.5 pr-7 appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors
                      ${currentMapping ? 'border-primary/40 text-foreground' : 'border-border text-muted-foreground'}`}
                  >
                    <option value="">— Skip this column —</option>
                    {CONTACT_FIELDS.map((f) => (
                      <option
                        key={f.key}
                        value={f.key}
                        disabled={usedFields.includes(f.key) && currentMapping !== f.key}
                      >
                        {f.label}{f.required ? ' *' : ''}
                        {usedFields.includes(f.key) && currentMapping !== f.key ? ' (used)' : ''}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                </div>

                {/* Sample value */}
                <span className="text-xs text-muted-foreground truncate" title={sample}>
                  {sample || <span className="italic text-muted-foreground/50">empty</span>}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Required fields reminder */}
      <p className="text-xs text-muted-foreground">
        <span className="text-red-500">*</span> Required fields: First Name, Last Name. At least one of Email or LinkedIn URL is recommended for duplicate detection.
      </p>
    </div>
  );
}
